import { useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, Modal, ScrollView, StyleSheet, Alert, ActivityIndicator } from 'react-native';
import * as ImagePicker from 'expo-image-picker';

interface BodyRecordInput {
  weight_kg: number;
  body_fat_pct?: number;
  measurements?: { waist?: number; arm?: number; hip?: number; thigh?: number };
}

interface Props {
  visible: boolean;
  onSave: (input: BodyRecordInput) => Promise<void>;
  onUploadPhoto: (uri: string) => Promise<void>;
  onClose: () => void;
}

export function BodyRecordForm({ visible, onSave, onUploadPhoto, onClose }: Props) {
  const [weight, setWeight] = useState('');
  const [fat, setFat] = useState('');
  const [waist, setWaist] = useState('');
  const [arm, setArm] = useState('');
  const [hip, setHip] = useState('');
  const [thigh, setThigh] = useState('');
  const [saving, setSaving] = useState(false);

  function reset() {
    setWeight(''); setFat(''); setWaist(''); setArm(''); setHip(''); setThigh('');
  }

  async function handleSave() {
    if (!weight || isNaN(parseFloat(weight))) {
      Alert.alert('Informe o peso'); return;
    }
    setSaving(true);
    try {
      const measurements: { waist?: number; arm?: number; hip?: number; thigh?: number } = {};
      if (waist) measurements.waist = parseFloat(waist);
      if (arm) measurements.arm = parseFloat(arm);
      if (hip) measurements.hip = parseFloat(hip);
      if (thigh) measurements.thigh = parseFloat(thigh);
      await onSave({
        weight_kg: parseFloat(weight),
        ...(fat ? { body_fat_pct: parseFloat(fat) } : {}),
        ...(Object.keys(measurements).length > 0 ? { measurements } : {}),
      });
      reset();
      onClose();
    } catch (e: any) {
      Alert.alert('Erro ao salvar', e.message);
    } finally {
      setSaving(false);
    }
  }

  async function handlePickPhoto() {
    const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (status !== 'granted') { Alert.alert('Permissão necessária'); return; }
    const result = await ImagePicker.launchImageLibraryAsync({ mediaTypes: ImagePicker.MediaTypeOptions.Images, quality: 0.8 });
    if (!result.canceled && result.assets[0]) {
      try {
        await onUploadPhoto(result.assets[0].uri);
        Alert.alert('Foto adicionada!');
      } catch (e: any) {
        Alert.alert('Erro ao enviar foto', e.message);
      }
    }
  }

  return (
    <Modal visible={visible} animationType="slide" transparent onRequestClose={onClose}>
      <View style={styles.overlay}>
        <ScrollView style={styles.sheet} contentContainerStyle={{ padding: 24 }} keyboardShouldPersistTaps="handled">
          <Text style={styles.title}>Registrar progresso</Text>

          <Text style={styles.label}>Peso (kg) *</Text>
          <TextInput style={styles.input} value={weight} onChangeText={setWeight} keyboardType="decimal-pad" placeholder="Ex: 75.5" />

          <Text style={styles.label}>% Gordura</Text>
          <TextInput style={styles.input} value={fat} onChangeText={setFat} keyboardType="decimal-pad" placeholder="Ex: 18.5" />

          <Text style={styles.sectionLabel}>Medidas (cm)</Text>
          <View style={styles.measureRow}>
            {[
              { label: 'Cintura', value: waist, set: setWaist },
              { label: 'Braço', value: arm, set: setArm },
            ].map(f => (
              <View key={f.label} style={styles.measureField}>
                <Text style={styles.label}>{f.label}</Text>
                <TextInput style={styles.input} value={f.value} onChangeText={f.set} keyboardType="decimal-pad" placeholder="0" />
              </View>
            ))}
          </View>
          <View style={styles.measureRow}>
            {[
              { label: 'Quadril', value: hip, set: setHip },
              { label: 'Coxa', value: thigh, set: setThigh },
            ].map(f => (
              <View key={f.label} style={styles.measureField}>
                <Text style={styles.label}>{f.label}</Text>
                <TextInput style={styles.input} value={f.value} onChangeText={f.set} keyboardType="decimal-pad" placeholder="0" />
              </View>
            ))}
          </View>

          <TouchableOpacity style={styles.photoBtn} onPress={handlePickPhoto}>
            <Text style={styles.photoBtnText}>📷 Adicionar foto de progresso</Text>
          </TouchableOpacity>

          <TouchableOpacity style={styles.saveBtn} onPress={handleSave} disabled={saving}>
            {saving ? <ActivityIndicator color="#fff" /> : <Text style={styles.saveBtnText}>Salvar</Text>}
          </TouchableOpacity>
          <TouchableOpacity onPress={onClose} style={styles.cancelBtn}>
            <Text style={styles.cancelBtnText}>Cancelar</Text>
          </TouchableOpacity>
        </ScrollView>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'flex-end' },
  sheet: { backgroundColor: '#fff', borderTopLeftRadius: 20, borderTopRightRadius: 20, maxHeight: '90%' },
  title: { fontSize: 18, fontWeight: 'bold', color: '#1A1A1A', marginBottom: 20 },
  label: { fontSize: 13, color: '#555', marginBottom: 4 },
  sectionLabel: { fontSize: 13, fontWeight: '600', color: '#1A1A1A', marginTop: 12, marginBottom: 8 },
  input: { borderWidth: 1, borderColor: '#ddd', borderRadius: 8, padding: 12, fontSize: 15, marginBottom: 12, backgroundColor: '#fafafa', color: '#1A1A1A' },
  measureRow: { flexDirection: 'row', gap: 12 },
  measureField: { flex: 1 },
  photoBtn: { borderWidth: 1.5, borderColor: '#C8102E', borderRadius: 8, padding: 14, alignItems: 'center', marginTop: 4, marginBottom: 16 },
  photoBtnText: { color: '#C8102E', fontWeight: '600', fontSize: 14 },
  saveBtn: { backgroundColor: '#C8102E', padding: 14, borderRadius: 8, alignItems: 'center' },
  saveBtnText: { color: '#fff', fontWeight: 'bold', fontSize: 15 },
  cancelBtn: { marginTop: 12, alignItems: 'center', paddingBottom: 8 },
  cancelBtnText: { color: '#888', fontSize: 14 },
});
